import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Brain, User, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatMsg(text) {
  if (!text) return "";
  let html = text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-100">$1</strong>');
  html = html.replace(/^### (.*$)/gm, '<h3 class="text-base font-semibold text-slate-100 mt-4 mb-1.5 font-space">$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2 class="text-lg font-semibold text-slate-100 mt-5 mb-2 font-space">$1</h2>');
  html = html.replace(/^\d+\.\s+(.*$)/gm, '<li class="ml-4 mb-1.5 text-slate-300 list-decimal list-inside text-sm">$1</li>');
  html = html.replace(/^[-*]\s+(.*$)/gm, '<li class="ml-4 mb-1.5 text-slate-300 list-disc list-inside text-sm">$1</li>');
  html = html.replace(/\n\n/g, '</p><p class="text-sm text-slate-300 leading-relaxed mb-2">');
  html = html.replace(/\n/g, '<br/>');
  return `<p class="text-sm text-slate-300 leading-relaxed mb-2">${html}</p>`;
}

export default function AIChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/ai/chat`, { message: msg, session_id: sessionId });
      if (data.session_id) setSessionId(data.session_id);
      if (data.error) {
        setMessages((prev) => [...prev, { role: "ai", text: `Error: ${data.error}` }]);
      } else {
        setMessages((prev) => [...prev, { role: "ai", text: data.response }]);
      }
    } catch (e) {
      setMessages((prev) => [...prev, { role: "ai", text: "Failed to get response. Please try again." }]);
    }
    setLoading(false);
  };

  const suggestions = [
    "What are the key investment highlights for StarAndDaisy?",
    "Explain the repeat customer LTV trend and why it matters",
    "How does StarAndDaisy's unit economics compare to industry benchmarks?",
    "What is the channel strategy and margin profile?",
    "Walk me through the 5-year revenue projection",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-100px)]" data-testid="ai-chat-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight flex items-center gap-2">
          <Brain className="w-7 h-7 text-blue-400" /> AI Investor Analyst
        </h1>
        <p className="text-sm text-slate-400 mt-1">Ask questions about StarAndDaisy's business — cohort data, financials, strategy</p>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 border-slate-800 bg-slate-900/50 backdrop-blur-xl overflow-hidden flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="w-14 h-14 rounded-full bg-slate-800/80 flex items-center justify-center mb-4">
                <Brain className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="font-space text-base font-semibold text-slate-200 mb-1">Ask me anything about StarAndDaisy</h3>
              <p className="text-xs text-slate-500 max-w-sm mb-6">
                I have full context of cohort analytics, financial model, LTV/CAC metrics, channel performance, and business strategy.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-xl">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => { setInput(s); }}
                    className="text-[11px] text-slate-400 bg-slate-800/50 border border-slate-700/50 rounded-full px-3 py-1.5 hover:text-blue-400 hover:border-blue-800 transition-colors text-left"
                    data-testid={`suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${i}`}>
              {m.role === "ai" && (
                <div className="w-7 h-7 rounded-full bg-blue-600/20 border border-blue-800/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Brain className="w-3.5 h-3.5 text-blue-400" />
                </div>
              )}
              <div className={`max-w-[75%] rounded-lg px-4 py-3 ${
                m.role === "user"
                  ? "bg-blue-600/20 border border-blue-800/30 text-sm text-slate-200"
                  : "bg-slate-800/40 border border-slate-700/30"
              }`}>
                {m.role === "user" ? m.text : (
                  <div dangerouslySetInnerHTML={{ __html: formatMsg(m.text) }} />
                )}
              </div>
              {m.role === "user" && (
                <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-3.5 h-3.5 text-slate-300" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-blue-600/20 border border-blue-800/40 flex items-center justify-center flex-shrink-0">
                <Brain className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
              </div>
              <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing...
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </CardContent>

        {/* Input */}
        <div className="border-t border-slate-800 p-3">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about business metrics, strategy, financials..."
              className="flex-1 bg-slate-800/40 border-slate-700 text-slate-200 placeholder:text-slate-600 h-10 text-sm"
              data-testid="chat-input"
              disabled={loading}
            />
            <Button type="submit" disabled={loading || !input.trim()}
              className="bg-blue-600 hover:bg-blue-500 text-white h-10 px-4" data-testid="chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
